import {debounce} from 'lodash';

import {safeSessionStorage} from '../../util/localStorage';

export interface DebouncedSessionStorageOptions {
  debounce?: {waitMs: number} | {waitMs: number; maxWaitMs: number};
}

/**
 * createDebouncedSessionStorage builds a zustand persist storage over
 * sessionStorage. With `debounce` set, writes debounce so a keystroke burst
 * writes once, `maxWaitMs` caps the gap during continuous typing, and a
 * pagehide flush saves state when the tab reloads inside the debounce
 * window. Callers with no high-frequency writes (e.g. the queue store) can
 * omit `debounce` to write immediately, with no timer involved.
 */
export function createDebouncedSessionStorage({
  debounce: debounceOptions,
}: DebouncedSessionStorageOptions = {}) {
  // Pending writes are keyed so a burst coalesces to one flush per key and a
  // write to one key never drops another key's pending write.
  const pending = new Map<string, string>();
  const flush = () => {
    for (const [name, value] of pending) {
      safeSessionStorage.setItem(name, value);
    }
    pending.clear();
  };

  const scheduleFlush = debounceOptions
    ? debounce(flush, debounceOptions.waitMs, {
        ...('maxWaitMs' in debounceOptions
          ? {maxWait: debounceOptions.maxWaitMs}
          : {}),
        trailing: true,
      })
    : flush;

  if (typeof window !== 'undefined' && debounceOptions) {
    window.addEventListener('pagehide', flush);
  }

  return {
    getItem: (name: string) => safeSessionStorage.getItem(name),
    setItem: (name: string, value: string) => {
      pending.set(name, value);
      scheduleFlush();
    },
    removeItem: (name: string) => {
      // Drop any pending write to this key so a debounced flush can't land
      // after the delete and resurrect it.
      pending.delete(name);
      safeSessionStorage.removeItem(name);
    },
  };
}
